/* Copy do painel de publishers em `components/publishers/` (ADR-0811): ação em lote, provisão
 * admin-created, curadoria (slugs reservados/domínios de email públicos), trilha de atividade,
 * chaves de assinatura e a célula de stats de moderação. As rotas (`routes/publishers-list-page.tsx`,
 * `routes/publisher-detail-page.tsx`) e `ACTION_VERB`/`buildPublisherSummaryMetrics` (verbo de ação
 * e faixa-resumo do detalhe) ficam fora do escopo desta migração — têm a própria copy ou
 * permanecem pt-BR fixo, ver `content/sections/publishers-list.ts` e
 * `components/publishers/publisher-status-map.ts`.
 */
import type { PublisherAction } from '@planuze/admin-contracts';
import type { PublisherStatusLabels } from '../../components/publishers/publisher-status-map.js';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

export interface PublishersContent {
  readonly statusLabels: PublisherStatusLabels;
  readonly actionLabels: Readonly<Record<PublisherAction, string>>;
  readonly bulkAction: {
    readonly ariaLabel: string;
    readonly selectedCount: (count: number) => string;
    readonly apply: string;
    readonly clearSelection: string;
  };
  readonly provisionModal: {
    readonly title: string;
    readonly description: string;
    readonly slugLabel: string;
    readonly noReservedSlugs: string;
    readonly slugPlaceholder: string;
    readonly slugHint: string;
    readonly displayNameLabel: string;
    readonly displayNamePlaceholder: string;
    readonly ownerLabel: string;
    readonly ownerPlaceholder: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: (slug: string) => string;
  };
  readonly provisionErrors: {
    readonly ownerNotFound: string;
    readonly invalidSlug: string;
    readonly slugTaken: string;
    readonly alreadyPublisher: string;
    readonly conflict: string;
    readonly unauthorized: string;
    readonly network: string;
    readonly generic: string;
    readonly unexpected: string;
  };
  readonly publicEmailDomains: {
    readonly title: string;
    readonly description: string;
    readonly loadError: string;
    readonly empty: string;
    readonly removeAriaLabel: (domain: string) => string;
    readonly placeholder: string;
    readonly add: string;
    readonly invalidDomain: string;
    readonly duplicateDomain: string;
    readonly saveError: string;
  };
  readonly reservedSlugs: {
    readonly title: string;
    readonly description: string;
    readonly loadError: string;
    readonly empty: string;
    readonly removeAriaLabel: (slug: string) => string;
    readonly placeholder: string;
    readonly add: string;
    readonly invalidSlug: string;
    readonly duplicateSlug: string;
    readonly saveError: string;
  };
  readonly activityTable: {
    /** Rótulo do botão que copia o id de quem executou a ação. */
    readonly actorIdLabel: string;
    readonly actionLabels: Readonly<Record<string, string>>;
    readonly targetLabels: Readonly<Record<string, string>>;
    readonly headers: {
      readonly when: string;
      readonly action: string;
      readonly target: string;
      readonly actor: string;
      readonly ip: string;
    };
    readonly emptyTitle: string;
    readonly emptyDescription: string;
  };
  readonly keysCard: {
    readonly title: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly rootSigned: string;
    readonly awaitingRootSign: string;
    readonly revoked: string;
  };
  readonly statsCell: {
    readonly noSubmissions: string;
    readonly summaryLine: (total: number, pending: number) => string;
    readonly rejectionLine: (percent: number, avgLabel: string | null) => string;
  };
  readonly table: {
    readonly headers: {
      readonly publisher: string;
      readonly status: string;
      readonly keys: string;
      readonly moderation: string;
    };
    readonly selectAllAriaLabel: string;
    readonly selectRowAriaLabel: (slug: string) => string;
    readonly noResultsTitle: string;
    readonly noResultsDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly keysSummary: (signed: number, pending: number) => string;
  };
}

const PUBLISHERS_CONTENT: Bilingual<PublishersContent> = {
  'pt-BR': {
    statusLabels: {
      verified: 'Verificado',
      banned: 'Banido',
      suspended: 'Suspenso',
      pending: 'Pendente',
    },
    actionLabels: {
      verify: 'Verificar',
      suspend: 'Suspender',
      unsuspend: 'Reativar',
      ban: 'Banir',
      unban: 'Desbanir',
    },
    bulkAction: {
      ariaLabel: 'Ação em lote',
      selectedCount: (count) => `${count} selecionado(s)`,
      apply: 'Aplicar',
      clearSelection: 'Limpar seleção',
    },
    provisionModal: {
      title: 'Provisionar publisher',
      description:
        'Atribui um namespace RESERVADO a um publisher, nomeando o owner inicial (usuário ' +
        'existente). O publisher nasce pendente; verifique e root-assine as chaves em seguida.',
      slugLabel: 'Slug reservado',
      noReservedSlugs:
        'Nenhum slug reservado disponível. Adicione um em “Slugs reservados” antes de provisionar.',
      slugPlaceholder: 'Selecionar slug reservado...',
      slugHint:
        'Vira o prefixo dos packs (ex.: planuze/nep). A lista é gerida em “Slugs reservados”.',
      displayNameLabel: 'Nome de exibição',
      displayNamePlaceholder: 'Planuze',
      ownerLabel: 'Owner inicial',
      ownerPlaceholder: 'Selecionar owner...',
      cancel: 'Cancelar',
      submit: 'Provisionar',
      success: (slug) => `Publisher "${slug}" provisionado.`,
    },
    provisionErrors: {
      ownerNotFound: 'Usuário owner não encontrado. Selecione um usuário existente.',
      invalidSlug: 'Slug inválido. Use apenas letras minúsculas, números e hífens.',
      slugTaken: 'Este slug já pertence a outro publisher.',
      alreadyPublisher: 'O usuário selecionado já é dono de um publisher.',
      conflict: 'Conflito ao criar (slug ou owner já em uso). Revise os dados.',
      unauthorized: 'Você não tem permissão para provisionar publishers.',
      network: 'Falha de conexão. Tente novamente.',
      generic: 'Não foi possível provisionar o publisher. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
    publicEmailDomains: {
      title: 'Domínios de email públicos',
      description:
        'Domínios que NÃO podem virar o domínio verificado de uma org (evita que toda conta ' +
        '@domínio entre por same-domain auto-join). A lista é gerida aqui (config-service) — ' +
        'nada hardcoded. Remova um domínio (ex.: gmail.com) para permitir cadastrar org com ele.',
      loadError: 'Falha ao carregar os domínios públicos.',
      empty: 'Nenhum domínio bloqueado.',
      removeAriaLabel: (domain) => `Remover ${domain}`,
      placeholder: 'exemplo.com',
      add: 'Adicionar',
      invalidDomain: 'Domínio inválido. Use o formato "exemplo.com" (minúsculas).',
      duplicateDomain: 'Este domínio já está na lista.',
      saveError: 'Não foi possível salvar os domínios públicos.',
    },
    reservedSlugs: {
      title: 'Slugs reservados',
      description:
        'Namespaces protegidos do onboard self-service (anti-squatting). Só provisionáveis a um ' +
        'publisher pelo backoffice. A lista é gerida aqui (config-service) — nada hardcoded.',
      loadError: 'Falha ao carregar os slugs reservados.',
      empty: 'Nenhum slug reservado.',
      removeAriaLabel: (slug) => `Remover ${slug}`,
      placeholder: 'novo-slug',
      add: 'Adicionar',
      invalidSlug: 'Slug inválido. Use minúsculas, números e hífen (sem hífen nas pontas).',
      duplicateSlug: 'Este slug já está reservado.',
      saveError: 'Não foi possível salvar os slugs reservados.',
    },
    activityTable: {
      actorIdLabel: 'ID do ator',
      actionLabels: {
        'token.created': 'Token criado',
        'token.revoked': 'Token revogado',
        'key.registered': 'Chave registrada',
        'key.revoked': 'Chave revogada',
        'key.retired': 'Chave aposentada',
        'domain.verified': 'Domínio verificado',
      },
      targetLabels: { token: 'Token', key: 'Chave', domain: 'Domínio' },
      headers: { when: 'Quando', action: 'Ação', target: 'Alvo', actor: 'Ator', ip: 'IP' },
      emptyTitle: 'Sem atividade',
      emptyDescription:
        'Eventos de tokens, chaves e verificação de domínio deste publisher aparecem aqui.',
    },
    keysCard: {
      title: 'Chaves de assinatura',
      emptyTitle: 'Nenhuma chave',
      emptyDescription: 'O publisher ainda não registrou uma chave de assinatura.',
      rootSigned: 'Root-assinada',
      awaitingRootSign: 'Aguardando root-sign',
      revoked: 'Revogada',
    },
    statsCell: {
      noSubmissions: 'Sem submissões',
      summaryLine: (total, pending) =>
        pending > 0 ? `${total} total, ${pending} pendente(s)` : `${total} total`,
      rejectionLine: (percent, avgLabel) =>
        avgLabel !== null ? `Rejeição ${percent}% · média ${avgLabel}` : `Rejeição ${percent}%`,
    },
    table: {
      headers: {
        publisher: 'Publisher',
        status: 'Status',
        keys: 'Chaves',
        moderation: 'Moderação',
      },
      selectAllAriaLabel: 'Selecionar todos',
      selectRowAriaLabel: (slug) => `Selecionar ${slug}`,
      noResultsTitle: 'Nenhum resultado',
      noResultsDescription: 'Nenhum publisher corresponde à busca ou ao filtro.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Nenhum publisher',
      emptyDescription: 'Publishers que fizerem onboarding aparecem aqui para governança.',
      keysSummary: (signed, pending) =>
        pending > 0 ? `${signed} assinada(s), ${pending} pendente(s)` : `${signed} assinada(s)`,
    },
  },
  'en-US': {
    statusLabels: {
      verified: 'Verified',
      banned: 'Banned',
      suspended: 'Suspended',
      pending: 'Pending',
    },
    actionLabels: {
      verify: 'Verify',
      suspend: 'Suspend',
      unsuspend: 'Reactivate',
      ban: 'Ban',
      unban: 'Unban',
    },
    bulkAction: {
      ariaLabel: 'Bulk action',
      selectedCount: (count) => `${count} selected`,
      apply: 'Apply',
      clearSelection: 'Clear selection',
    },
    provisionModal: {
      title: 'Provision publisher',
      description:
        'Assigns a RESERVED namespace to a publisher, naming the initial owner (an existing ' +
        'user). The publisher starts pending; verify and root-sign the keys next.',
      slugLabel: 'Reserved slug',
      noReservedSlugs:
        'No reserved slug available. Add one in "Reserved slugs" before provisioning.',
      slugPlaceholder: 'Select reserved slug...',
      slugHint:
        'Becomes the packs prefix (e.g., planuze/nep). The list is managed in "Reserved slugs".',
      displayNameLabel: 'Display name',
      displayNamePlaceholder: 'Planuze',
      ownerLabel: 'Initial owner',
      ownerPlaceholder: 'Select owner...',
      cancel: 'Cancel',
      submit: 'Provision',
      success: (slug) => `Publisher "${slug}" provisioned.`,
    },
    provisionErrors: {
      ownerNotFound: 'Owner user not found. Select an existing user.',
      invalidSlug: 'Invalid slug. Use only lowercase letters, numbers, and hyphens.',
      slugTaken: 'This slug already belongs to another publisher.',
      alreadyPublisher: 'The selected user already owns a publisher.',
      conflict: 'Conflict while creating (slug or owner already in use). Review the data.',
      unauthorized: 'You do not have permission to provision publishers.',
      network: 'Connection failure. Try again.',
      generic: 'Could not provision the publisher. Try again.',
      unexpected: 'Unexpected error.',
    },
    publicEmailDomains: {
      title: 'Public email domains',
      description:
        "Domains that CANNOT become an org's verified domain (prevents every @domain account " +
        'from entering via same-domain auto-join). The list is managed here (config-service) — ' +
        'nothing hardcoded. Remove a domain (e.g., gmail.com) to allow registering an org with it.',
      loadError: 'Failed to load the public domains.',
      empty: 'No blocked domains.',
      removeAriaLabel: (domain) => `Remove ${domain}`,
      placeholder: 'example.com',
      add: 'Add',
      invalidDomain: 'Invalid domain. Use the "example.com" format (lowercase).',
      duplicateDomain: 'This domain is already in the list.',
      saveError: 'Could not save the public domains.',
    },
    reservedSlugs: {
      title: 'Reserved slugs',
      description:
        'Namespaces protected from self-service onboarding (anti-squatting). Only provisionable ' +
        'to a publisher via the backoffice. The list is managed here (config-service) — nothing ' +
        'hardcoded.',
      loadError: 'Failed to load the reserved slugs.',
      empty: 'No reserved slugs.',
      removeAriaLabel: (slug) => `Remove ${slug}`,
      placeholder: 'new-slug',
      add: 'Add',
      invalidSlug:
        'Invalid slug. Use lowercase letters, numbers, and hyphens (no hyphen at the ends).',
      duplicateSlug: 'This slug is already reserved.',
      saveError: 'Could not save the reserved slugs.',
    },
    activityTable: {
      actorIdLabel: 'Actor ID',
      actionLabels: {
        'token.created': 'Token created',
        'token.revoked': 'Token revoked',
        'key.registered': 'Key registered',
        'key.revoked': 'Key revoked',
        'key.retired': 'Key retired',
        'domain.verified': 'Domain verified',
      },
      targetLabels: { token: 'Token', key: 'Key', domain: 'Domain' },
      headers: { when: 'When', action: 'Action', target: 'Target', actor: 'Actor', ip: 'IP' },
      emptyTitle: 'No activity',
      emptyDescription:
        'Token, key, and domain verification events for this publisher appear here.',
    },
    keysCard: {
      title: 'Signing keys',
      emptyTitle: 'No keys',
      emptyDescription: 'The publisher has not registered a signing key yet.',
      rootSigned: 'Root-signed',
      awaitingRootSign: 'Awaiting root-sign',
      revoked: 'Revoked',
    },
    statsCell: {
      noSubmissions: 'No submissions',
      summaryLine: (total, pending) =>
        pending > 0 ? `${total} total, ${pending} pending` : `${total} total`,
      rejectionLine: (percent, avgLabel) =>
        avgLabel !== null ? `Rejection ${percent}% · avg ${avgLabel}` : `Rejection ${percent}%`,
    },
    table: {
      headers: { publisher: 'Publisher', status: 'Status', keys: 'Keys', moderation: 'Moderation' },
      selectAllAriaLabel: 'Select all',
      selectRowAriaLabel: (slug) => `Select ${slug}`,
      noResultsTitle: 'No results',
      noResultsDescription: 'No publisher matches the search or filter.',
      clearFilters: 'Clear filters',
      emptyTitle: 'No publishers',
      emptyDescription: 'Publishers that complete onboarding appear here for governance.',
      keysSummary: (signed, pending) =>
        pending > 0 ? `${signed} signed, ${pending} pending` : `${signed} signed`,
    },
  },
};

export const usePublishersContent = (): PublishersContent => useBilingual(PUBLISHERS_CONTENT);
